import {Component, inject, PLATFORM_ID, signal, WritableSignal} from '@angular/core';
import {AuctionsOfferService} from './auctions-offer.service';
import {AuctionDaysLeftAccent, EndingInfo, PageItem, PropertyValues, SearchRequest} from './auctions-offer.types';
import {Observable, of} from 'rxjs';
import {AsyncPipe, isPlatformBrowser, NgClass} from '@angular/common';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {phosphorEngineBold} from '@ng-icons/phosphor-icons/bold';
import {tablerManualGearbox, tablerRoad} from '@ng-icons/tabler-icons';
import {
  matFactoryOutline,
  matGroups2Outline,
  matLocalGasStationOutline,
  matLocationOnOutline,
  matTimerOutline
} from '@ng-icons/material-icons/outline';
import {Carousel, CarouselPageEvent} from 'primeng/carousel';
import {matArrowForwardRound} from '@ng-icons/material-icons/round';
import {phosphorGlobe} from '@ng-icons/phosphor-icons/regular';
import {
  AuctionsOfferModalComponent,
  CarOfferData,
  CarOfferFormData
} from './auctions-offer-modal/auctions-offer-modal.component';
import {ToastType} from '../../../shared/toast/toast-type.enum';
import {ToastService} from '../../../shared/toast/toast.service';
import {ReCaptchaV3Service} from 'ngx-captcha';
import {environment} from '../../../environments/environment';
import {AuctionsSpecialOfferFormRequest} from '../../../shared/model/auctions-special-offer-form-request.model';
import {RichResultsService} from '../../../shared/rich-results/rich-results.service';
import {
  buildItemList,
  buildListItem,
  buildOffer,
  buildProduct
} from '../../../shared/rich-results/rich-results.builders';
import {ListItem} from '../../../shared/rich-results/rich-results';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AvailabilityType} from '../../../shared/enum/rich-results-types.enum';
import {PixelService} from '../../../shared/analytics/meta/pixel.service';
import {GoogleAnalyticsService} from '../../../shared/analytics/google/google-analytics.service';
import {
  AnalyticsEventCategory,
  AnalyticsEventName,
  FormEventSendResult
} from '../../../shared/enum/gtm-cookie-consent.enum';
import {FormSubmitStatus, PixelEventName} from '../../../shared/analytics/meta/types/pixel.types';
import {isSpecialOfferSignal} from '../../../shared/signal/auctions/special-offer-component.signal';

@Component({
  selector: 'app-auctions-offer',
  templateUrl: './auctions-offer.component.html',
  styleUrl: './auctions-offer.component.scss',
  imports: [
    AsyncPipe,
    NgIcon,
    NgClass,
    Carousel,
    AuctionsOfferModalComponent
  ],
  providers: [provideIcons({
      phosphorEngineBold,
      tablerManualGearbox,
      matGroups2Outline,
      tablerRoad,
      matLocalGasStationOutline,
      matTimerOutline,
      matLocationOnOutline,
      matFactoryOutline,
      matArrowForwardRound,
      phosphorGlobe
    }
  )]
})
export class AuctionsOfferComponent {

  protected auctions$: Observable<PageItem[]> = of([]);
  private readonly body: SearchRequest
  protected selectedCarData: CarOfferData | undefined;
  protected isModalOpen: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly isSpecialOfferSignal = isSpecialOfferSignal;
  protected readonly recaptchaSiteKey = environment.recaptcha.siteKey;
  private readonly TOAST_TITLE: string = "Zapytanie o ofertę";
  private readonly TOAST_CONTENT_SUCCESS = "Zapytanie wysłane"
  private readonly TOAST_CONTENT_ERROR = "Błąd w wysyłaniu zapytania"
  private readonly CAPTCHA_ID: string = "auctions_special_offer_form";
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly isBrowser = isPlatformBrowser(this.platformId);

  responsiveOptions = [
    {
      breakpoint: '1400px',
      numVisible: 3,
      numScroll: 3
    },
    {
      breakpoint: '1200px',
      numVisible: 2,
      numScroll: 2
    },
    {
      breakpoint: '768px',
      numVisible: 1,
      numScroll: 1
    }
  ];

  constructor(
    private readonly auctionsOfferService: AuctionsOfferService,
    private readonly toastService: ToastService,
    private readonly recaptchaService: ReCaptchaV3Service,
    private readonly richResultsService: RichResultsService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly pixelService: PixelService
  ) {
    this.body = {
      filters: {
        type: {values: [], type: 'list'},
        SUBTYPE: {values: [], type: 'list'},
        ENGINE_MILEAGE: {from: null, to: null, type: 'between'},
        CONDITION: {values: ['NEW', 'USED'], type: 'list'},
        YEAR_OF_PRODUCTION: {from: null, to: null, type: 'between'},
        OFFERING_TYPE: {values: ['special-offer-offering'], type: 'list'},
        OFFERING_SPECIAL_OFFER_INSTALLMENT: {from: null, to: null, type: 'between'},
        AUCTION_BUY_NOW: {value: null, type: 'equals'},
        OFFERING_ENDING_DATE: {from: null, to: null, type: 'between'}
      },
      sorting: {
        AUCTION_BID_COUNT: 'DESC'
      }
    }

    this.auctions$ = this.auctionsOfferService.search(this.body);

    this.auctions$.pipe(takeUntilDestroyed())
      .subscribe(auctions => {
        if (!auctions?.length) {
          isSpecialOfferSignal.set(false);
        } else {
          isSpecialOfferSignal.set(true);
        }
      });

    this.setupRichResults();
  }

  protected getImageUrl(offerId: string, imageId: string): string {
    return `https://api.licytacje.processway.pl/public/listing/${offerId}/pictures/${imageId}?scaleType=scale&scaleHeight=500&scaleWidth=500`;
  }

  protected auctionEndingInfoResolver(endingDate: string): EndingInfo {
    const days = this.resolveOfferDaysLeft(endingDate);

    let accent: AuctionDaysLeftAccent;
    if (days > 7) {
      accent = AuctionDaysLeftAccent.Ordinary;
    } else if (days > 3) {
      accent = AuctionDaysLeftAccent.Warn;
    } else {
      accent = AuctionDaysLeftAccent.Urgent;
    }

    const polishDaysLabel = (n: number): string => {
      const abs = Math.abs(n);
      if (abs === 1) return '1 dzień';
      const mod10 = abs % 10;
      const mod100 = abs % 100;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${abs} dni`;
      return `${abs} dni`;
    };

    return {
      daysLeft: polishDaysLabel(days),
      accent
    };
  }

  protected onCarSelected(carOffer: PageItem): void {
    this.selectedCarData = {
      id: carOffer.id,
      name: carOffer.name,
      offerGallery: carOffer.pictures,
      installment: carOffer.metadata.offering.specialOffer.installment,
      offerEndingInfo: this.auctionEndingInfoResolver(carOffer.metadata.offering.endingDate)
    };

    this.googleAnalyticsService.trackEvent({
      event: AnalyticsEventName.AuctionsSpecialOfferClick,
      eventCategory: AnalyticsEventCategory.Carousel,
      eventPayload: `offerId: ${carOffer.id}`
    });

    this.pixelService.trackEvent(
      PixelEventName.CarouselOfferSelected,
      {
        offerId: carOffer.id
      }
    );

    this.isModalOpen.set(true);
  }

  protected onSubmit($event: CarOfferFormData): void {
    this.onClose();
    this.selectedCarData = undefined;

    this.toastService.fireToast(ToastType.INFO, "Zapytanie", "Wysyłanie zapytania");

    this.recaptchaService.execute(this.recaptchaSiteKey, this.CAPTCHA_ID, (token) => {
      const source = $event;
      const carGallery = source.carGallery.map(image => image.fileId);
      const payload: AuctionsSpecialOfferFormRequest = {
        fullName: source.fullName,
        company: source.company,
        email: source.email,
        phone: source.phone,
        offerId: source.offerId,
        carName: source.carName,
        carGallery: carGallery,
        carInstallment: source.carInstallment.toString(),
        offerDaysLeft: source.offerDaysLeft,
        consentDataProcessing: source.consentDataProcessing,
        consentPrivacyPolicy: source.consentPrivacyPolicy,
        recaptchaToken: token
      };

      this.auctionsOfferService.sendSpecialOfferContactMessage(payload)
        .subscribe({
          next: (response) => {
            if (response.ok) {
              this.toastService.fireToast(ToastType.SUCCESS, this.TOAST_TITLE, this.TOAST_CONTENT_SUCCESS);

              this.googleAnalyticsService.trackEvent({
                event: AnalyticsEventName.AuctionsSpecialOfferFormSubmit,
                eventCategory: AnalyticsEventCategory.SpecialOffer,
                eventPayload: {
                  offerId: source.offerId,
                  status: FormEventSendResult.Success
                }
              });

              this.pixelService.trackEvent(PixelEventName.SpecialOfferFormSubmit, {
                offerId: source.offerId,
                status: FormSubmitStatus.SUCCESS
              });
            }
          },
          error: () => {
            this.toastService.fireToast(ToastType.ERROR, this.TOAST_TITLE, this.TOAST_CONTENT_ERROR);

            this.googleAnalyticsService.trackEvent({
              event: AnalyticsEventName.AuctionsSpecialOfferFormSubmit,
              eventCategory: AnalyticsEventCategory.SpecialOffer,
              eventPayload: {
                offerId: source.offerId,
                status: FormEventSendResult.Error
              }
            });

            this.pixelService.trackEvent(PixelEventName.SpecialOfferFormSubmit, {
              offerId: source.offerId,
              status: FormSubmitStatus.ERROR
            });
          }
        });
    });
  }

  protected onClose(): void {
    this.isModalOpen.set(false);
    this.selectedCarData = undefined;
  }

  private resolveOfferDaysLeft(endingDate: string): number {
    const parsedDate = new Date(endingDate);
    const now = new Date();
    const diffMs = parsedDate.getTime() - now.getTime();

    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }

  protected onDetailsNavigation($offer: CarOfferData) {
    const offerId = $offer.id;
    const url = `https://licytacje.processway.pl/item/${offerId}`;

    this.googleAnalyticsService.trackEvent({
      event: AnalyticsEventName.AuctionsSpecialOfferDetailsClick,
      eventCategory: AnalyticsEventCategory.Carousel,
      eventPayload: `offerId: ${offerId}`
    });

    this.pixelService.trackEvent(PixelEventName.CarouselOfferDetails, {
      offerId: offerId
    });

    window.open(url, '_blank', 'noopener');
  }

  private getBasicDescription(props: PropertyValues): string {
    return [
      `Marka: ${props.BRAND}`,
      `Model: ${props.MODEL}`,
      `Rodzaj paliwa: ${props.FUEL_TYPE}`,
      `Typ nadwozia: ${props.BODY_TYPE}`,
      `Skrzynia biegów: ${props.TRANSMISSION_TYPE}`,
      `Liczba miejsc: ${props.NUMBER_OF_SEATS}`,
      `Data pierwszej rejestracji: ${props.FIRST_REGISTRATION_DATE}`,
      `Rok produkcji: ${props.YEAR_OF_PRODUCTION}`,
      `Przebieg: ${props.ENGINE_MILEAGE} km`,
      `Podtyp: ${props.SUBTYPE}`,
      `Moc: ${props.POWER_HP} KM`,
      `Pojemność silnika: ${props.DISPLACEMENT} cm3`
    ].join(', ');
  }

  private setupRichResults(): void {
    this.auctions$
      .pipe(takeUntilDestroyed())
      .subscribe(auctions => {
        if (!auctions?.length) return;

        const listItems: ListItem[] = auctions.map((auction, index) => {
          const offer = buildOffer({
            price: auction.metadata.offering.specialOffer.installment,
            priceCurrency: 'PLN',
            availability: AvailabilityType.InStock,
            priceValidUntil: auction.metadata.offering.endingDate
          })

          const product = buildProduct({
            name: auction.name,
            description: this.getBasicDescription(auction.propertyValues),
            image: this.getImageUrl(auction.id, auction.pictures[1].fileId),
            sku: auction.id,
            brand: auction.propertyValues.BRAND,
            offers: offer,
            url: `https://licytacje.processway.pl/item/${auction.id}`
          });

          return buildListItem({
            position: index + 1,
            item: product
          })
        });

        const carousel = buildItemList({
            itemListElement: listItems
          }
        );

        this.richResultsService.register(carousel);
      });
  }

  protected onPageEvent($event: CarouselPageEvent) {
    if ($event.page !== 0) {
      this.googleAnalyticsService.trackEvent({
        event: AnalyticsEventName.AuctionsSpecialOfferCarouselSwipe,
        eventCategory: AnalyticsEventCategory.Carousel,
        eventPayload: `page: ${$event.page}`
      });

      this.pixelService.trackEvent(
        PixelEventName.CarouselSwipe, {
          page: $event.page
        });
    }
  }
}
